import React, { useState, useEffect } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { db, firebaseConfig } from '../../firebase/config/firebase';
import { University, StudentProfile } from '../../types';
import { universityService } from '../../services/universityService';
import { invalidateAdminStudentsCache } from '../../services/adminBookingService';
import { sendStudentRegistrationNtfyNotification } from '../../services/ntfyService';
import {
  UserPlus,
  Phone,
  Mail,
  KeyRound,
  GraduationCap,
  Bus,
  MapPin,
  Send,
  Copy,
  CheckCircle2,
  RefreshCw,
  Sparkles,
  ExternalLink,
  MessageSquare,
  ShieldCheck,
} from 'lucide-react';
import './AdminPages.css';

interface CreatedAccountSummary {
  fullName: string;
  phoneNumber: string;
  email: string;
  password: string;
  universityName: string;
  assignedRoute: string;
  pickupLocation: string;
  createdAt: string;
}

export const AdminStudentAccountCreator: React.FC = () => {
  const [universities, setUniversities] = useState<University[]>([]);
  const [isLoadingUnis, setIsLoadingUnis] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Form Fields
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedUniId, setSelectedUniId] = useState('');
  const [assignedRoute, setAssignedRoute] = useState('');
  const [pickupLocation, setPickupLocation] = useState('الموقف الجديد بمحرم بيك');

  // Last Created Account for Instant WhatsApp Handover
  const [lastCreated, setLastCreated] = useState<CreatedAccountSummary | null>(null);
  const [recentAccounts, setRecentAccounts] = useState<CreatedAccountSummary[]>([]);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Load Universities cleanly
  useEffect(() => {
    const fetchUnis = async () => {
      setIsLoadingUnis(true);
      try {
        const list = await universityService.getAllUniversities();
        setUniversities(list);
        if (list[0]) {
          setSelectedUniId(list[0].id);
          setAssignedRoute(`الموقف الجديد بمحرم بيك ◄ ${list[0].name}`);
        }
      } catch (err) {
        console.warn('Could not load universities in student account creator:', err);
      } finally {
        setIsLoadingUnis(false);
      }
    };

    fetchUnis();
  }, []);

  // Update default assigned route when university changes
  const handleUniChange = (uniId: string) => {
    setSelectedUniId(uniId);
    const uni = universities.find((u) => u.id === uniId);
    if (uni) {
      setAssignedRoute(`الموقف الجديد بمحرم بيك ◄ ${uni.name}`);
    }
  };

  // Generate a friendly strong password
  const generateRandomPassword = () => {
    const num = Math.floor(100000 + Math.random() * 900000);
    const pass = `Oto${num}!`;
    setPassword(pass);
  };

  // Format clean phone for WhatsApp international URL
  const formatWhatsAppPhone = (phone: string) => {
    let cleaned = phone.replace(/[^0-9]/g, '');
    if (cleaned.startsWith('0')) {
      cleaned = '2' + cleaned;
    } else if (!cleaned.startsWith('20') && cleaned.length === 10) {
      cleaned = '20' + cleaned;
    }
    return cleaned;
  };

  // Compose formatted WhatsApp message
  const composeWhatsAppMessage = (acc: CreatedAccountSummary) => {
    return `السلام عليكم ورحمة الله وبركاته يا ${acc.fullName}،
أهلاً بك في منظومة OTO STUDENTS لنقل طلاب الجامعات 🚌🎓

تم إنشاء وتفعيل حسابك بنجاح في المنظومة:
👤 اسم المستخدم / البريد: ${acc.email}
🔑 كلمة المرور: ${acc.password}
🎓 الجامعة: ${acc.universityName}
🛣️ خط السير المعين: ${acc.assignedRoute}
📍 نقطة الركوب بالإسكندرية: ${acc.pickupLocation}

━━━━━━━━━━━━━━━━━━━━━
📢 تنويه وملاحظات هامة بخصوص الرحلات والأسعار:
1️⃣ موعد انطلاق أولى الرحلات: يبدأ السفر بإذن الله يوم الأحد 1 نوفمبر، وباب الحجز مفتوح من الآن لاختيار مقعدك!
2️⃣ نظام التسعير التوفيري وضمان استرداد الفارق:
• تبدأ الرحلات بسيارات تويوتا هاي إس (HiAce) مكيفة حديثة موديل 2026 سعة 13 راكباً.
• عند وصول عدد الطلاب الحاجزين إلى 33 طالباً، يتم الترقية فوراً إلى ميني باص 33 راكباً بسعر تذكرة مخفض.
• وعند وصول 50 طالباً يتم استخدام حافلة كبرى 50 راكباً بسعر أوفر بكثير!
✨ إذا قمت بالحجز مسبقاً بالسعر الأولي وتم تخفيض السعر، سيتم استرداد فارق السعر لك بالكامل فوراً.

📲 رابط تسجيل الدخول لمتابعة وحجز رحلاتك ومقعدك:
https://otostudents.com/login

نتمنى لك رحلات مريحة وآمنة وفصلاً دراسياً موفقاً مع OTO STUDENTS! ✨`;
  };

  // Send to WhatsApp
  const handleSendWhatsApp = (acc: CreatedAccountSummary) => {
    const cleanPhone = formatWhatsAppPhone(acc.phoneNumber);
    const message = composeWhatsAppMessage(acc);
    const encoded = encodeURIComponent(message);
    const url = `https://wa.me/${cleanPhone}?text=${encoded}`;
    window.open(url, '_blank');
  };

  // Copy to clipboard
  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 3000);
  };

  // Handle Submit Form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim()) {
      alert('يرجى إدخال اسم الطالب بالكامل.');
      return;
    }

    const cleanPhone = phoneNumber.replace(/[^0-9]/g, '');
    if (!cleanPhone) {
      alert('رقم الهاتف إجباري لإنشاء حساب الطالب.');
      return;
    }

    if (!/^01[0125][0-9]{8}$/.test(cleanPhone)) {
      alert('يرجى إدخال رقم هاتف محمول مصري صحيح مكون من 11 رقماً يبدأ بـ 010 أو 011 أو 012 أو 015.');
      return;
    }

    if (!email.trim() || !password.trim()) {
      alert('يرجى إدخال البريد الإلكتروني وكلمة المرور.');
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);

    const targetUni = universities.find((u) => u.id === selectedUniId) || universities[0];
    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password.trim();
    let uid = 'std_' + Math.random().toString(36).substring(2, 9);
    const nowIso = new Date().toISOString();

    // 1. Create student in Firebase Authentication using Secondary Auth instance (keeps admin logged in)
    try {
      const existingApps = getApps();
      const secondaryApp =
        existingApps.find((a) => a.name === 'SecondaryStudentAuth') ||
        initializeApp(firebaseConfig, 'SecondaryStudentAuth');
      const secondaryAuth = getAuth(secondaryApp);
      
      const authUserCred = await createUserWithEmailAndPassword(
        secondaryAuth,
        cleanEmail,
        cleanPassword
      );
      if (authUserCred?.user?.uid) {
        uid = authUserCred.user.uid;
      }
      await signOut(secondaryAuth);
    } catch (authErr: any) {
      console.warn('Secondary auth notice (will be auto-bootstrapped on student first login):', authErr);
    }

    const studentRecord: StudentProfile = {
      uid,
      fullName: fullName.trim(),
      phoneNumber: phoneNumber.trim(),
      email: cleanEmail,
      address: pickupLocation.trim(),
      universityId: targetUni?.id || 'uni_cairo',
      universityName: targetUni?.name || 'جامعة القاهرة',
      universityGovernorate: targetUni?.governorate || 'الجيزة',
      assignedRoute: assignedRoute.trim(),
      password: cleanPassword,
      status: 'ACTIVE',
      profileCompleted: true,
      createdAt: nowIso,
      updatedAt: nowIso,
    };

    const summary: CreatedAccountSummary = {
      fullName: fullName.trim(),
      phoneNumber: phoneNumber.trim(),
      email: cleanEmail,
      password: cleanPassword,
      universityName: targetUni?.name || 'جامعة القاهرة',
      assignedRoute: assignedRoute.trim(),
      pickupLocation: pickupLocation.trim(),
      createdAt: nowIso,
    };

    try {
      const docRef = doc(db, 'students', uid);
      await setDoc(docRef, studentRecord, { merge: true });
      invalidateAdminStudentsCache();

      // Dispatch real-time ntfy.sh notification with student basic details (Name, Phone, University)
      sendStudentRegistrationNtfyNotification({
        uid,
        fullName: summary.fullName,
        phoneNumber: summary.phoneNumber,
        universityName: summary.universityName,
        email: summary.email,
        assignedRoute: summary.assignedRoute,
        address: summary.pickupLocation,
        createdAt: nowIso,
        source: 'ADMIN_CREATED',
      }).catch((nErr) => console.warn('Non-blocking admin student creator ntfy dispatch failed:', nErr));

      setLastCreated(summary);
      setRecentAccounts((prev) => [summary, ...prev.slice(0, 4)]);
      setFeedback({
        text: `تم إنشاء وتفعيل حساب الطالب "${summary.fullName}" بنجاح في المنظومة وتعيين خط السير!`,
        type: 'success',
      });

      // Reset form fields
      setFullName('');
      setPhoneNumber('');
      setEmail('');
      setPassword('');
    } catch (err: any) {
      console.warn('Fallback local student account creation:', err);
      setLastCreated(summary);
      setRecentAccounts((prev) => [summary, ...prev.slice(0, 4)]);
      setFeedback({
        text: `تم حفظ حساب الطالب وتجهيز رسالة الواتساب.`,
        type: 'success',
      });
      setFullName('');
      setPhoneNumber('');
      setEmail('');
      setPassword('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-page-container" dir="rtl">
      {/* Header */}
      <div className="admin-page-header">
        <div className="admin-header-title-box">
          <div className="flex items-center gap-2 mb-1">
            <span className="admin-badge-purple">قسم الإدارة التشغيلية</span>
            <span className="text-xs bg-emerald-50 text-emerald-800 font-bold px-2 py-0.5 rounded-full border border-emerald-200">
              WhatsApp Integration
            </span>
          </div>
          <h2>
            <UserPlus size={24} className="text-[#E5232A]" />
            <span>إنشاء حسابات الطلاب وتعيين خطوط السير</span>
          </h2>
          <p>
            تسجيل حسابات الطلاب الجدد وتعيينهم في خطوط السير الثابتة، مع إمكانية إرسال بيانات الدخول والباسورد مباشرة للواتساب.
          </p>
        </div>
      </div>

      {feedback && (
        <div
          className={`p-3.5 rounded-xl border mb-5 flex items-center gap-2 ${
            feedback.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
              : 'bg-red-50 text-red-800 border-red-200'
          }`}
        >
          <CheckCircle2 size={18} />
          <span className="font-bold text-sm">{feedback.text}</span>
        </div>
      )}

      {/* Grid: Creation Form & Last Created WhatsApp Card */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
        {/* Left/Main Form: 7 cols */}
        <div className="lg:col-span-7 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between pb-4 mb-4 border-b">
            <h3 className="font-bold text-slate-800 text-base flex items-center gap-2">
              <UserPlus size={18} className="text-[#E5232A]" />
              <span>بيانات حساب الطالب الجديد</span>
            </h3>
            <button
              type="button"
              className="text-xs text-[#E5232A] font-bold hover:underline flex items-center gap-1"
              onClick={generateRandomPassword}
            >
              <Sparkles size={13} />
              <span>توليد كلمة سر عشوائية</span>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="admin-form-group mb-3">
              <label>اسم الطالب بالكامل رباعياً *</label>
              <input
                type="text"
                required
                placeholder="مثال: يوسف أحمد محمود عبد الحميد"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>

            <div className="form-grid-2 mb-3">
              <div className="admin-form-group">
                <label className="flex items-center justify-between gap-1">
                  <div className="flex items-center gap-1">
                    <Phone size={13} className="text-[#E5232A]" />
                    <span>رقم الهاتف (الواتساب)</span>
                  </div>
                  <span className="text-[#E5232A] text-xs font-bold">* إجباري</span>
                </label>
                <input
                  type="tel"
                  required
                  placeholder="مثال: 01012345678"
                  dir="ltr"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              </div>

              <div className="admin-form-group">
                <label className="flex items-center gap-1">
                  <Mail size={13} className="text-slate-400" />
                  <span>البريد الإلكتروني للدخول *</span>
                </label>
                <input
                  type="email"
                  required
                  placeholder="مثال: student@gmail.com"
                  dir="ltr"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="admin-form-group mb-3">
              <div className="flex items-center justify-between mb-1">
                <label className="flex items-center gap-1">
                  <KeyRound size={13} className="text-slate-400" />
                  <span>كلمة المرور للحساب (Password) *</span>
                </label>
                <button
                  type="button"
                  className="text-[11px] text-[#E5232A] font-bold"
                  onClick={generateRandomPassword}
                >
                  إنشاء كلمة سر قوية
                </button>
              </div>
              <input
                type="text"
                required
                placeholder="مثال: Oto2026! أو كلمة سر الطالب"
                dir="ltr"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <div className="form-grid-2 mb-3">
              <div className="admin-form-group">
                <label className="flex items-center gap-1">
                  <GraduationCap size={13} className="text-slate-400" />
                  <span>الجامعة المقيد بها *</span>
                </label>
                <select
                  value={selectedUniId}
                  onChange={(e) => handleUniChange(e.target.value)}
                  required
                  disabled={isLoadingUnis}
                >
                  {universities.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.governorate})
                    </option>
                  ))}
                </select>
              </div>

              <div className="admin-form-group">
                <label className="flex items-center gap-1 justify-between">
                  <span className="flex items-center gap-1">
                    <Bus size={13} className="text-slate-400" />
                    <span>خط السير المعين / الخط الثابت *</span>
                  </span>
                  <span className="text-[10px] text-slate-500 font-bold">
                    محطات الانطلاق: الموقف الجديد بمحرم بيك أو الكيلو 21
                  </span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="مثال: الموقف الجديد بمحرم بيك ◄ جامعة السادات"
                  value={assignedRoute}
                  onChange={(e) => setAssignedRoute(e.target.value)}
                />
                {(() => {
                  const currUni = universities.find((u) => u.id === selectedUniId);
                  const uName = currUni?.name || 'الجامعة';
                  return (
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <button
                        type="button"
                        onClick={() => {
                          setAssignedRoute(`الموقف الجديد بمحرم بيك ◄ ${uName}`);
                          setPickupLocation('الموقف الجديد بمحرم بيك');
                        }}
                        className="text-[11px] bg-slate-100 hover:bg-rose-50 hover:text-rose-700 text-slate-700 px-2 py-0.5 rounded font-semibold transition-colors"
                      >
                        📍 الموقف الجديد بمحرم بيك ◄ {uName}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setAssignedRoute(`الكيلو 21 ◄ ${uName}`);
                          setPickupLocation('الكيلو 21');
                        }}
                        className="text-[11px] bg-slate-100 hover:bg-rose-50 hover:text-rose-700 text-slate-700 px-2 py-0.5 rounded font-semibold transition-colors"
                      >
                        📍 الكيلو 21 ◄ {uName}
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="admin-form-group mb-4">
              <label className="flex items-center gap-1">
                <MapPin size={13} className="text-slate-400" />
                <span>نقطة الركوب والانطلاق بالإسكندرية</span>
              </label>
              <div className="flex items-center gap-2 mb-1.5">
                <button
                  type="button"
                  onClick={() => setPickupLocation('الموقف الجديد بمحرم بيك')}
                  className={`text-xs px-2.5 py-1 rounded-lg border font-bold transition-all ${
                    pickupLocation === 'الموقف الجديد بمحرم بيك'
                      ? 'bg-rose-50 border-rose-300 text-rose-700'
                      : 'bg-slate-50 border-slate-200 text-slate-600'
                  }`}
                >
                  الموقف الجديد بمحرم بيك
                </button>
                <button
                  type="button"
                  onClick={() => setPickupLocation('الكيلو 21')}
                  className={`text-xs px-2.5 py-1 rounded-lg border font-bold transition-all ${
                    pickupLocation === 'الكيلو 21'
                      ? 'bg-rose-50 border-rose-300 text-rose-700'
                      : 'bg-slate-50 border-slate-200 text-slate-600'
                  }`}
                >
                  الكيلو 21
                </button>
              </div>
              <input
                type="text"
                placeholder="محطة الانطلاق (الموقف الجديد بمحرم بيك أو الكيلو 21 أو تغيير الإدارة)"
                value={pickupLocation}
                onChange={(e) => setPickupLocation(e.target.value)}
              />
            </div>

            <button
              type="submit"
              className="btn-admin-primary w-full py-3 text-sm font-bold flex items-center justify-center gap-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  <span>جاري إنشاء الحساب...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 size={18} />
                  <span>حفظ وإنشاء الحساب وتجهيز رسالة الواتساب</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Right WhatsApp Dispatch Panel: 5 cols */}
        <div className="lg:col-span-5 flex flex-col gap-4">
          {lastCreated ? (
            <div className="bg-emerald-50 border border-emerald-200 p-5 rounded-2xl shadow-sm">
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-emerald-200">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold">
                    ✓
                  </div>
                  <div>
                    <strong className="text-emerald-900 block text-sm">تم إنشاء الحساب بنجاح!</strong>
                    <span className="text-xs text-emerald-700">{lastCreated.fullName}</span>
                  </div>
                </div>
                <span className="text-xs bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full">
                  جاهز للإرسال
                </span>
              </div>

              {/* Message Preview Box */}
              <div className="bg-white p-3.5 rounded-xl border border-emerald-200 mb-4 text-xs font-mono leading-relaxed text-slate-800 whitespace-pre-wrap max-h-64 overflow-y-auto">
                {composeWhatsAppMessage(lastCreated)}
              </div>

              {/* WhatsApp Action Buttons */}
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-sm"
                  onClick={() => handleSendWhatsApp(lastCreated)}
                >
                  <Send size={15} />
                  <span>إرسال البيانات عبر الواتساب الآن (WhatsApp)</span>
                  <ExternalLink size={13} />
                </button>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    className="py-2 px-3 bg-white border border-emerald-300 hover:bg-emerald-100 text-emerald-800 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-all"
                    onClick={() => handleCopy(composeWhatsAppMessage(lastCreated), 'msg')}
                  >
                    <Copy size={13} />
                    <span>{copiedKey === 'msg' ? 'تم النسخ ✓' : 'نسخ الرسالة'}</span>
                  </button>

                  <button
                    type="button"
                    className="py-2 px-3 bg-white border border-emerald-300 hover:bg-emerald-100 text-emerald-800 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-all"
                    onClick={() => handleCopy(lastCreated.password, 'pass')}
                  >
                    <KeyRound size={13} />
                    <span>{copiedKey === 'pass' ? 'تم نسخ الباسورد ✓' : 'نسخ الباسورد'}</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 p-6 rounded-2xl text-center shadow-sm flex flex-col items-center justify-center min-h-[300px]">
              <div className="w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3">
                <MessageSquare size={32} />
              </div>
              <h4 className="font-bold text-slate-800 text-base mb-1">إرسال بيانات الدخول عبر الواتساب</h4>
              <p className="text-xs text-slate-500 max-w-xs leading-relaxed mb-4">
                بمجرد حفظ بيانات الطالب، سيتم تلقائياً تجهيز رسالة الواتساب المتكاملة متضمنة اسم المستخدم، كلمة المرور، الجامعة، وخط السير المحدد.
              </p>
              <div className="flex items-center gap-1.5 text-xs text-slate-400 font-mono">
                <ShieldCheck size={14} className="text-emerald-500" />
                <span>حسابات نشطة ومحمية عبر OTO STUDENTS</span>
              </div>
            </div>
          )}

          {/* Quick instructions card */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-xs text-slate-600">
            <strong className="block text-slate-800 mb-1.5 font-bold">💡 ملاحظات تشغيلية للإدارة:</strong>
            <ul className="list-disc list-inside space-y-1">
              <li>يستطيع الطالب فور استلام الرسالة تسجيل الدخول عبر الموقع وتصفح رحلات خطه.</li>
              <li>يمكنك في أي وقت تغيير كلمة مرور الطالب من جدول الطلاب المسجلين.</li>
              <li>يتم تخصيص المقاعد للطلاب تلقائياً بالترتيب في حافلات الـ 50 راكباً.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Recently Created Accounts Section */}
      {recentAccounts.length > 0 && (
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
          <h4 className="font-bold text-slate-800 text-sm mb-3 flex items-center gap-2">
            <Sparkles size={16} className="text-[#E5232A]" />
            <span>الحسابات التي تم إنشاؤها مؤخراً في هذه الجلسة</span>
          </h4>
          <div className="overflow-x-auto">
            <table className="admin-data-table">
              <thead>
                <tr>
                  <th>اسم الطالب</th>
                  <th>رقم الهاتف</th>
                  <th>البريد الإلكتروني</th>
                  <th>كلمة المرور</th>
                  <th>الجامعة وخط السير</th>
                  <th>إرسال واتساب</th>
                </tr>
              </thead>
              <tbody>
                {recentAccounts.map((acc, idx) => (
                  <tr key={idx}>
                    <td>
                      <strong className="text-slate-800">{acc.fullName}</strong>
                    </td>
                    <td>
                      <span className="font-mono text-xs" dir="ltr">
                        {acc.phoneNumber}
                      </span>
                    </td>
                    <td>
                      <span className="font-mono text-xs text-slate-600" dir="ltr">
                        {acc.email}
                      </span>
                    </td>
                    <td>
                      <span className="font-mono text-xs font-bold text-[#E5232A]" dir="ltr">
                        {acc.password}
                      </span>
                    </td>
                    <td>
                      <div>
                        <strong className="block text-xs text-slate-800">{acc.universityName}</strong>
                        <span className="text-[11px] text-slate-500">{acc.assignedRoute}</span>
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="py-1 px-3 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all"
                        onClick={() => handleSendWhatsApp(acc)}
                        title="إرسال بيانات الحساب للواتساب"
                      >
                        <Send size={12} />
                        <span>واتساب</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
